import React, { useState, useEffect } from 'react';
import { InputV2 } from '../InputV2/InputV2';
import { useAliasValidation } from '../../hooks/useAliasValidation';
import BookingPlansAPI from '../../api/BookingPlansAPI';
import { Check, X } from 'lucide-react';

interface BookingLinkInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  baseUrl?: string;
  onValidationChange?: (isValid: boolean) => void;
}

const BookingLinkInput: React.FC<BookingLinkInputProps> = ({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  baseUrl,
  onValidationChange,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);
  
  const { validateAliasFormat, debouncedCheckAvailability, isValidationInProgress, currentAbortController } = useAliasValidation();

  const validateLinkText = (text: string, clearAvailabilityError: boolean = false): boolean => {
    const validation = validateAliasFormat(text);
    setError(validation.error);
    if (clearAvailabilityError) {
      setAvailabilityError(null);
    }
    return validation.isValid;
  };

  const handleLinkTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    onChange(newText);
    
    if (newText === value) {
      return;
    }
    
    // Reset validation state when user types
    setIsValid(false);
    setAvailabilityError(null);
    setHasValidated(false);
    
    const isValid = validateLinkText(newText, true);
    
    if (!isValid || newText.length < 3) {
      setIsValid(false);
      if (onValidationChange) {
        onValidationChange(false);
      }
      return;
    }
    
    debouncedCheckAvailability(
      newText,
      (alias: string) => BookingPlansAPI.checkAliasAvailability(alias, currentAbortController?.signal),
      (result) => {
        setHasValidated(true);
        if (!result.isAvailable) {
          setAvailabilityError(result.error || 'This alias is not available. Please try a different one.');
          setIsValid(false);
          if (onValidationChange) {
            onValidationChange(false);
          }
        } else {
          setAvailabilityError(null);
          setIsValid(true);
          if (onValidationChange) {
            onValidationChange(true);
          }
        }
      }
    );
  };

  useEffect(() => {
    if (!value || value.length < 3) {
      setIsValid(false);
      setHasValidated(false);
      if (onValidationChange) {
        onValidationChange(false);
      }
      return;
    }
    
    const formatValid = validateLinkText(value, false);
    if (!formatValid) {
      setIsValid(false);
      setHasValidated(false);
      if (onValidationChange) {
        onValidationChange(false);
      }
      return;
    }
    
    // Only trigger validation if we haven't validated yet or if there's an availability error
    if (!hasValidated || availabilityError) {
      debouncedCheckAvailability(
        value,
        (alias: string) => BookingPlansAPI.checkAliasAvailability(alias, currentAbortController?.signal),
        (result) => {
          setHasValidated(true);
          if (!result.isAvailable) {
            setAvailabilityError(result.error || 'This alias is not available. Please try a different one.');
            setIsValid(false);
            if (onValidationChange) {
              onValidationChange(false);
            }
          } else {
            setAvailabilityError(null);
            setIsValid(true);
            if (onValidationChange) {
              onValidationChange(true);
            }
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!value) {
      setIsValid(false);
      if (onValidationChange) {
        onValidationChange(false);
      }
      return;
    }
    
    const formatValid = validateLinkText(value, false);
    const isLongEnough = value.length >= 3;
    const isAvailable = !availabilityError;
    const currentIsValid = formatValid && isLongEnough && isAvailable && !isValidationInProgress;
    
    setIsValid(currentIsValid);
    if (onValidationChange) {
      onValidationChange(currentIsValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityError, isValidationInProgress]);

  const displayBaseUrl = baseUrl || `${window.location.origin}/bookings/`;

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center space-x-2">
        <span className="text-gray-500 text-sm whitespace-nowrap">
          {displayBaseUrl}
        </span>
        <div className="flex-1 relative">
          <InputV2
            type="text"
            value={value}
            onChange={handleLinkTextChange}
            placeholder={placeholder}
            disabled={disabled}
            className={`flex-1 ${error || availabilityError ? 'border-red-300 focus:border-red-500' : isValid ? 'border-green-300 focus:border-green-500' : ''}`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isValidationInProgress && (
              <div className="w-4 h-4 border-2 border-darker-gold-300 border-t-transparent rounded-full animate-spin"></div>
            )}
            {!isValidationInProgress && value && value.length >= 3 && (
              <>
                {hasValidated && isValid && !error && !availabilityError && (
                  <Check className="w-5 h-5 text-green-500" />
                )}
                {hasValidated && (error || availabilityError) && (
                  <X className="w-5 h-5 text-red-500" />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {error && (
        <p className="text-red-500 text-xs">{error}</p>
      )}
      {availabilityError && (
        <p className="text-red-500 text-xs">{availabilityError}</p>
      )}
      {!error && !availabilityError && value && value.length >= 3 && !isValidationInProgress && (
        <p className="text-xs text-gray-500">
          This will be your shareable booking link. You can customize it later.
        </p>
      )}
    </div>
  );
};

export default BookingLinkInput;

